import { Controller } from "@hotwired/stimulus"

// Quiz controller — state machine pra tablet 10".
// Estados: welcome → form → question → suspense → correct/wrong →
//          roulette → win → (reset → welcome)
//
// Roletas: PREMIUM (acerto, 1/12 jackpot R$150) ou CONSOLATION (uniforme R$10/15/20).
export default class extends Controller {
  static targets = [
    "step", "emailInput", "nameInput", "phoneInput",
    "submitButton", "formError",
    "questionCategory", "questionText", "questionOptions",
    "correctOption", "confetti",
    "rouletteSvg", "rouletteContainer", "spinButton",
    "rouletteTitle", "rouletteSubtitle",
    "winIcon", "winAmount", "winText",
    "paymentDoneButton", "paymentDoneHint", "resetButton",
    "errorMessage"
  ]

  static values = {
    startUrl: String,
    answerUrl: String,
    spinUrl: String,
    markPaidUrl: String,
    csrf: String,
    premiumWheel: Array,
    consolationWheel: Array
  }

  IDLE_RESET_MS = 60_000

  connect() {
    this.currentStep = "welcome"
    this.session = null
    this._beep = this._createBeep()
    this._installKioskGuards()
  }

  disconnect() {
    this._uninstallKioskGuards()
    this._clearIdleTimer()
  }

  goToForm() { this._goto("form"); this.emailInputTarget.focus() }
  goToRoulette() { this._goto("roulette"); this._renderWheel() }

  reset() {
    this.session = null
    if (this.hasEmailInputTarget) this.emailInputTarget.value = ""
    if (this.hasNameInputTarget) this.nameInputTarget.value = ""
    if (this.hasPhoneInputTarget) this.phoneInputTarget.value = ""
    if (this.hasFormErrorTarget) { this.formErrorTarget.hidden = true; this.formErrorTarget.textContent = "" }
    this._goto("welcome")
  }

  async submitForm(event) {
    event.preventDefault()
    const email = this.emailInputTarget.value.trim()
    if (!email) return this._formError("Informe seu email")

    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = "Carregando…"

    try {
      const res = await fetch(this.startUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({
          email,
          full_name: this.nameInputTarget.value.trim(),
          phone: this.phoneInputTarget.value.trim()
        })
      })
      const body = await res.json()
      if (!res.ok) {
        this._formError(body.error || "Erro ao iniciar quiz")
        return
      }
      this.session = { id: body.session_id, question: body.question, alreadyComplete: body.user_already_complete }
      this._renderQuestion(body.question)
      this._goto("question")
    } catch (e) {
      this._formError("Erro de rede. Tente novamente.")
    } finally {
      this.submitButtonTarget.disabled = false
      this.submitButtonTarget.textContent = "Iniciar quiz →"
    }
  }

  _renderQuestion(q) {
    this.questionCategoryTarget.textContent = q.category || ""
    this.questionTextTarget.textContent = q.text
    this.questionOptionsTarget.innerHTML = ""
    q.options.forEach((opt, idx) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.className = "quiz-option"
      btn.dataset.action = "click->quiz#chooseAnswer"
      btn.dataset.index = idx
      btn.innerHTML = `<span class="quiz-option-letter">${String.fromCharCode(65 + idx)}</span><span class="quiz-option-text"></span>`
      btn.querySelector(".quiz-option-text").textContent = opt
      this.questionOptionsTarget.appendChild(btn)
    })
  }

  async chooseAnswer(event) {
    const btn = event.currentTarget
    const idx = parseInt(btn.dataset.index, 10)
    if (Number.isNaN(idx)) return

    this.questionOptionsTarget.querySelectorAll(".quiz-option").forEach(b => {
      b.disabled = true
      b.classList.toggle("is-selected", b === btn)
    })

    this._goto("suspense")

    try {
      const res = await fetch(this.answerUrlValue, {
        method: "POST",
        headers: this._headers(),
        body: JSON.stringify({ chosen_index: idx })
      })
      const body = await res.json()
      if (!res.ok) {
        this._showError(body.error || "Erro ao enviar resposta")
        return
      }

      await this._sleep(1500)

      this.session.correctIndex = body.correct_index
      this.session.correctOption = body.correct_option
      this.session.wasCorrect = body.was_correct
      this.session.wheelKind = body.wheel_kind

      if (body.was_correct) {
        this._fireConfetti()
        this._goto("correct")
      } else {
        this.correctOptionTarget.textContent = body.correct_option
        this._goto("wrong")
      }
    } catch (e) {
      this._showError("Erro de rede. Tente novamente.")
    }
  }

  _renderWheel() {
    const wheel = this.session.wheelKind === "premium"
      ? this.premiumWheelValue
      : this.consolationWheelValue
    const sliceCount = wheel.length
    const sliceAngle = 360 / sliceCount
    const cx = 250, cy = 250, r = 240

    const svg = this.rouletteSvgTarget
    svg.innerHTML = `
      <defs>
        <linearGradient id="jackpotGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stop-color="#ffd700"/>
          <stop offset="50%" stop-color="#ffb300"/>
          <stop offset="100%" stop-color="#ff8f00"/>
        </linearGradient>
      </defs>
    `
    const wheelGroup = document.createElementNS("http://www.w3.org/2000/svg", "g")
    wheelGroup.setAttribute("class", "roulette-rotor")
    wheelGroup.style.transformOrigin = "250px 250px"

    for (let i = 0; i < sliceCount; i++) {
      const slice = wheel[i]
      const startAngle = i * sliceAngle
      const path = this._slicePath(cx, cy, r, startAngle, sliceAngle)
      const isJackpot = slice.color === "jackpot"
      const fill = isJackpot ? "url(#jackpotGradient)" : slice.color

      const pathEl = document.createElementNS("http://www.w3.org/2000/svg", "path")
      pathEl.setAttribute("d", path)
      pathEl.setAttribute("fill", fill)
      pathEl.setAttribute("stroke", isJackpot ? "#ffd700" : "rgba(0,0,0,0.25)")
      pathEl.setAttribute("stroke-width", isJackpot ? "2.5" : "1.5")
      wheelGroup.appendChild(pathEl)

      const centerAngle = startAngle + sliceAngle / 2
      const tx = cx + r * 0.68 * Math.cos((centerAngle - 90) * Math.PI / 180)
      const ty = cy + r * 0.68 * Math.sin((centerAngle - 90) * Math.PI / 180)
      const text = document.createElementNS("http://www.w3.org/2000/svg", "text")
      text.setAttribute("x", tx)
      text.setAttribute("y", ty)
      text.setAttribute("text-anchor", "middle")
      text.setAttribute("dominant-baseline", "middle")
      text.setAttribute("fill", "#fff")
      text.setAttribute("font-weight", "900")
      text.setAttribute("font-size", isJackpot ? "26" : "22")
      text.setAttribute("transform", `rotate(${centerAngle} ${tx} ${ty})`)
      text.style.paintOrder = "stroke"
      text.style.stroke = "rgba(0,0,0,0.5)"
      text.style.strokeWidth = "2.5"
      text.textContent = `R$${slice.value / 100}`
      wheelGroup.appendChild(text)
    }
    svg.appendChild(wheelGroup)

    const center = document.createElementNS("http://www.w3.org/2000/svg", "circle")
    center.setAttribute("cx", cx)
    center.setAttribute("cy", cy)
    center.setAttribute("r", "42")
    center.setAttribute("fill", "#0b1218")
    center.setAttribute("stroke", "rgba(255,255,255,0.18)")
    center.setAttribute("stroke-width", "2.5")
    svg.appendChild(center)

    if (this.session.wheelKind === "premium") {
      this.rouletteTitleTarget.textContent = "🎰 Roleta de Prêmios"
      this.rouletteSubtitleTarget.textContent = "Gire pra ver quanto você vai ganhar!"
    } else {
      this.rouletteTitleTarget.textContent = "🎁 Roleta Surpresa"
      this.rouletteSubtitleTarget.textContent = "Você não acertou, mas ainda ganha algo!"
    }

    this.spinButtonTarget.disabled = false
    this.spinButtonTarget.textContent = "Girar a roleta!"
  }

  _slicePath(cx, cy, r, startDeg, sweepDeg) {
    const start = (startDeg - 90) * Math.PI / 180
    const end = (startDeg + sweepDeg - 90) * Math.PI / 180
    const x1 = cx + r * Math.cos(start), y1 = cy + r * Math.sin(start)
    const x2 = cx + r * Math.cos(end), y2 = cy + r * Math.sin(end)
    return `M ${cx} ${cy} L ${x1} ${y1} A ${r} ${r} 0 ${+(sweepDeg > 180)} 1 ${x2} ${y2} Z`
  }

  async spinRoulette() {
    if (this._spinning) return
    this._spinning = true
    this.spinButtonTarget.disabled = true
    this.spinButtonTarget.textContent = "Girando…"

    try {
      const res = await fetch(this.spinUrlValue, { method: "POST", headers: this._headers() })
      const body = await res.json()
      if (!res.ok) {
        this._showError(body.error || "Erro ao girar roleta")
        return
      }

      const wheel = this.session.wheelKind === "premium"
        ? this.premiumWheelValue : this.consolationWheelValue
      const sliceAngle = 360 / wheel.length
      const wonIdx = body.index
      const wonValue = body.value_cents
      this.session.paymentMode = body.payment_mode || "tablet"

      const targetAngle = -(wonIdx * sliceAngle + sliceAngle / 2)
      const jitter = (Math.random() - 0.5) * (sliceAngle * 0.6)
      const totalRotation = 360 * 6 + targetAngle + jitter

      const rotor = this.rouletteSvgTarget.querySelector(".roulette-rotor")
      const startTime = performance.now()
      const duration = 7000
      let lastSliceIndex = -1

      const animate = (now) => {
        const t = Math.min(1, (now - startTime) / duration)
        const eased = 1 - Math.pow(1 - t, 5)
        const angle = totalRotation * eased
        rotor.style.transform = `rotate(${angle}deg)`

        const normalizedAngle = ((angle % 360) + 360) % 360
        const sliceUnderPointer = Math.floor((-normalizedAngle / sliceAngle % wheel.length + wheel.length) % wheel.length)
        if (sliceUnderPointer !== lastSliceIndex) {
          if (lastSliceIndex !== -1) this._beep()
          lastSliceIndex = sliceUnderPointer
        }

        if (t < 1) {
          requestAnimationFrame(animate)
        } else {
          setTimeout(() => this._showWin(wonValue), 1500)
        }
      }
      requestAnimationFrame(animate)
    } catch (e) {
      this._showError("Erro de rede. Tente novamente.")
    } finally {
      this._spinning = false
    }
  }

  _showWin(cents) {
    const reais = (cents / 100).toFixed(2).replace(".", ",").replace(/,00$/, "")
    this.winAmountTarget.textContent = `R$ ${reais}`
    if (cents >= 15000) this.winIconTarget.textContent = "🏆"
    else if (cents >= 5000) this.winIconTarget.textContent = "🎉"
    else this.winIconTarget.textContent = "🎁"

    if (this.session?.alreadyComplete) {
      this.winTextTarget.innerHTML = "Mostre essa tela ao promoter para receber via Pix."
    } else {
      this.winTextTarget.innerHTML = "Mostre essa tela ao promoter para receber via Pix.<br>Confira seu email pra criar sua conta no OpinaKI!"
    }

    // Mostra botão apropriado conforme payment_mode da setting
    const mode = this.session?.paymentMode || "tablet"
    if (mode === "tablet") {
      this.paymentDoneButtonTarget.hidden = false
      this.paymentDoneHintTarget.hidden = false
      this.resetButtonTarget.hidden = true
    } else {
      this.paymentDoneButtonTarget.hidden = true
      this.paymentDoneHintTarget.hidden = true
      this.resetButtonTarget.hidden = false
    }

    this._goto("win")
    // Não auto-reseta no modo tablet (precisa de ação explícita do promoter)
    if (mode === "app") this._scheduleIdleReset()
  }

  // POST /quiz/mark_paid (modo "tablet" — promoter confirmou PIX)
  async paymentDone() {
    if (this._payingDone) return
    this._payingDone = true
    this.paymentDoneButtonTarget.disabled = true
    this.paymentDoneButtonTarget.textContent = "Registrando…"

    try {
      const res = await fetch(this.markPaidUrlValue, { method: "POST", headers: this._headers() })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        this._showError(body.error || "Erro ao registrar pagamento")
        return
      }
      // Sucesso: reset pra welcome (próximo participante)
      this.reset()
    } catch (e) {
      this._showError("Erro de rede. Tente novamente.")
    } finally {
      this._payingDone = false
      if (this.hasPaymentDoneButtonTarget) {
        this.paymentDoneButtonTarget.disabled = false
        this.paymentDoneButtonTarget.textContent = "✓ PAGAMENTO CONCLUÍDO"
      }
    }
  }

  _goto(step) {
    this.currentStep = step
    this.stepTargets.forEach(el => {
      el.classList.toggle("quiz-step--active", el.dataset.step === step)
    })
    window.scrollTo({ top: 0, behavior: "instant" })
  }

  _formError(msg) {
    this.formErrorTarget.textContent = msg
    this.formErrorTarget.hidden = false
  }

  _showError(msg) {
    this.errorMessageTarget.textContent = msg
    this._goto("error")
  }

  _headers() {
    return {
      "Content-Type": "application/json",
      "Accept": "application/json",
      "X-CSRF-Token": this.csrfValue
    }
  }

  _sleep(ms) { return new Promise(r => setTimeout(r, ms)) }

  _createBeep() {
    let ctx = null
    return (freq = 1400) => {
      try {
        ctx ||= new (window.AudioContext || window.webkitAudioContext)()
        const osc = ctx.createOscillator()
        const gain = ctx.createGain()
        osc.type = "square"
        osc.frequency.setValueAtTime(freq, ctx.currentTime)
        gain.gain.setValueAtTime(0.07, ctx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.04)
        osc.connect(gain).connect(ctx.destination)
        osc.start()
        osc.stop(ctx.currentTime + 0.05)
      } catch (_) {}
    }
  }

  _fireConfetti() {
    if (!this.hasConfettiTarget) return
    this.confettiTarget.innerHTML = ""
    const colors = [ "#2ed184", "#01a3ff", "#ffd93d", "#c084fc", "#fb923c" ]
    for (let i = 0; i < 30; i++) {
      const piece = document.createElement("div")
      piece.className = "confetti-piece"
      piece.style.setProperty("--x", `${Math.random() * 100}%`)
      piece.style.setProperty("--delay", `${Math.random() * 1.2}s`)
      piece.style.setProperty("--color", colors[i % colors.length])
      piece.style.setProperty("--size", `${6 + Math.random() * 8}px`)
      piece.style.setProperty("--drift", `${(Math.random() - 0.5) * 120}px`)
      this.confettiTarget.appendChild(piece)
    }
    setTimeout(() => { if (this.hasConfettiTarget) this.confettiTarget.innerHTML = "" }, 4500)
  }

  _scheduleIdleReset() {
    this._clearIdleTimer()
    this._idleTimer = setTimeout(() => this.reset(), this.IDLE_RESET_MS)
  }
  _clearIdleTimer() { if (this._idleTimer) { clearTimeout(this._idleTimer); this._idleTimer = null } }

  _installKioskGuards() {
    this._onKey = (e) => {
      const blocked = (
        e.key === "F11" || e.key === "F12" ||
        (e.ctrlKey && [ "l", "L", "t", "T", "n", "N", "w", "W" ].includes(e.key)) ||
        (e.ctrlKey && e.shiftKey && [ "i", "I", "j", "J" ].includes(e.key)) ||
        (e.metaKey && [ "l", "L", "t", "T", "n", "N", "w", "W" ].includes(e.key))
      )
      if (blocked) { e.preventDefault(); e.stopPropagation() }
    }
    this._onCtx = (e) => e.preventDefault()
    document.addEventListener("keydown", this._onKey, true)
    document.addEventListener("contextmenu", this._onCtx, true)
  }
  _uninstallKioskGuards() {
    if (this._onKey) document.removeEventListener("keydown", this._onKey, true)
    if (this._onCtx) document.removeEventListener("contextmenu", this._onCtx, true)
  }
}
